import { Check } from 'lucide-react'

import { THEMES, THEME_NAMES, deriveTheme, type ThemeChoice } from '../../theme'
import { cx } from '../../ui'

/*
 | El selector de tema.
 |
 | Cada opción se pinta CON su propio tema y con el color que el estudio tiene
 | puesto ahora mismo. No es un adorno: la pregunta que se está haciendo el
 | fotógrafo no es «¿cómo se llama este tema?» sino «¿cómo va a quedar mi
 | naranja aquí?», y una lista de nombres no la responde.
 |
 | La derivación es la misma función que usa el motor en tiempo de ejecución,
 | así que lo que se ve aquí es exactamente lo que verá su cliente.
 */

interface Props {
  value: ThemeChoice
  /** El color que el estudio tiene puesto, para pintar las muestras. */
  brandColor: string
  onChange: (theme: ThemeChoice) => void
}

export function ThemePicker({ value, brandColor, onChange }: Props) {
  return (
    <fieldset className="border-0 p-0">
      <legend className="sr-only">Tema de la galería</legend>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Option
          selected={value === 'auto'}
          onSelect={() => onChange('auto')}
          label="Automático"
          description="Sigue el modo claro u oscuro del móvil de tu cliente."
          preview={deriveTheme(brandColor, undefined, THEMES.editorial).variables}
          /*
           | `auto` es la única opción que no tiene UN aspecto: depende del móvil
           | de quien abra el enlace. Enseñar sólo el claro —que es lo que hacía—
           | daba una muestra idéntica a «Editorial claro», y dos tarjetas que se
           | ven igual pero hacen cosas distintas es peor que no enseñar nada.
           | La segunda mitad, cortada en diagonal, dice «puede ser cualquiera de
           | las dos» sin necesidad de leer.
           */
          secondPreview={deriveTheme(brandColor, undefined, THEMES.galeria).variables}
        />

        {THEME_NAMES.map((name) => (
          <Option
            key={name}
            selected={value === name}
            onSelect={() => onChange(name)}
            label={THEMES[name].label}
            description={THEMES[name].description}
            preview={deriveTheme(brandColor, undefined, THEMES[name]).variables}
          />
        ))}
      </div>
    </fieldset>
  )
}

interface OptionProps {
  selected: boolean
  onSelect: () => void
  label: string
  description: string
  preview: Record<string, string>
  /** Sólo para `auto`: se superpone cortada en diagonal sobre la primera. */
  secondPreview?: Record<string, string>
}

/** Las únicas variables que usa la muestra. Pasarlas todas infla el `style`. */
const USED = [
  '--surface',
  '--surface-sunken',
  '--text',
  '--text-subtle',
  '--accent',
  '--accent-contrast',
] as const

function scope(preview: Record<string, string>): Record<string, string> {
  return Object.fromEntries(USED.map((key) => [key, preview[key] ?? '']))
}

function Option({
  selected,
  onSelect,
  label,
  description,
  preview,
  secondPreview,
}: OptionProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cx(
        'flex flex-col overflow-hidden rounded-card border text-left transition-colors',
        selected ? 'border-accent ring-1 ring-accent' : 'border-line hover:border-line-strong',
      )}
    >
      <span className="relative block">
        <Sample style={scope(preview)} />

        {secondPreview !== undefined && (
          <>
            <Sample
              style={scope(secondPreview)}
              className="absolute inset-0"
              // El corte va de arriba-derecha a abajo-izquierda: deja la mitad
              // clara arriba a la izquierda, que es por donde se empieza a mirar.
              clip="polygon(100% 0, 100% 100%, 0 100%)"
            />
            {/* Filo visible: sin él, dos temas de luminosidad parecida se
                funden y el corte deja de leerse como intencionado. */}
            <span
              aria-hidden
              className="absolute inset-0 bg-[linear-gradient(to_top_right,transparent_calc(50%-0.5px),rgb(255_255_255/.35)_50%,transparent_calc(50%+0.5px))]"
            />
          </>
        )}
      </span>

      <span className="flex flex-1 flex-col gap-0.5 border-t border-line p-3">
        <span className="flex items-center gap-1.5 text-label font-medium text-ink">
          {label}
          {selected && <Check size={14} strokeWidth={2.5} className="text-accent" aria-hidden />}
        </span>
        <span className="text-caption text-ink-muted">{description}</span>
      </span>
    </button>
  )
}

/**
 * La galería en miniatura.
 *
 * Se pinta con las variables del tema que representa, no con las de la página.
 * De ahí el `style` en línea: es el único sitio del producto donde un
 * componente tiene que salirse a propósito del tema activo.
 */
function Sample({
  style,
  className,
  clip,
}: {
  style: Record<string, string>
  className?: string
  clip?: string
}) {
  return (
    <span
      style={clip === undefined ? style : { ...style, clipPath: clip }}
      className={cx('flex flex-col gap-2 bg-surface p-3', className)}
    >
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 shrink-0 rounded-full bg-accent" />
        <span className="h-1.5 flex-1 rounded-full bg-[rgb(var(--text-subtle))] opacity-40" />
      </span>

      <span className="grid grid-cols-3 gap-1">
        <span className="aspect-square rounded bg-surface-sunken" />
        <span className="aspect-square rounded bg-surface-sunken" />
        <span className="aspect-square rounded bg-surface-sunken" />
      </span>

      <span className="flex h-6 items-center justify-center rounded-md bg-accent text-micro font-medium text-accent-contrast">
        Descargar
      </span>
    </span>
  )
}
